<template lang='pug'>
  div
    v-dialog(v-model="isActive" v-if="recurrence" persistent max-width="500")
      v-card
        v-card-title.primary.widget-title Custom Recurrence
        v-card-text
          v-form
            v-container
              v-layout(row wrap)
                //- Repeat count
                v-flex(xs12)
                  span Repeat Every
                v-flex(xs2)
                  v-text-field(
                    v-model="recurrence.interval"
                    type="number"
                    min="1"
                  )
                v-flex(xs2)
                  v-select(
                    :items="this.recurrence.frequencies"
                    :item-text="item => item.name"
                    v-model="recurrence.frequency"
                  )

                //- The Days of the week selector if weekly frequency is chosen
                v-flex(xs12 v-if="recurrence.frequency === frequency.WEEKLY")
                  span Repeat on
                  v-layout(row wrap)
                    v-flex(v-for="weekday in weekdays" :key="weekday.id")
                      v-checkbox(
                        v-model="recurrence.byday"
                        :value="weekday.id"
                        :label="weekday.id"
                        @change="weekdaySelected"
                        multiple
                      )
                //- Selector for options if monthly frequency is chosen
                v-flex(xs12 v-if="recurrence.frequency === frequency.MONTHLY")
                  v-flex(xs10)
                    v-select(
                      v-model="recurrence.monthlyRepeatType"
                      :items="recurrence.monthlyRepeatTypes"
                      :item-text="item => item.description"
                      :item-value="item => item.key"
                    )

                //- Menu to choose ending date
                v-flex(xs12)
                  span Ends
                  v-radio-group(v-model="recurrence.endType")
                    v-layout(wrap)
                      //- Option 2 - Ends at a certain date
                      v-flex(xs12  v-if="!isCampaignSetup")
                        v-layout(align-center wrap)
                          v-flex(xs3 )
                            v-radio(:value="1" label="On")
                          v-flex(xs5)
                            v-menu(
                              ref="calendarMenu"
                              :close-on-content-click="false"
                              :disabled="recurrence.endType !== 1"
                              transition="scale-transition"
                              min-width="290px"
                              lazy
                              offset-y
                              full-width
                            )
                              v-text-field(
                                v-model="recurrence.until"
                                :disabled="recurrence.endType !== 1"
                                slot="activator"
                                label="Date"
                                prepend-icon="event"
                                readonly
                                clearable
                              )
                              v-date-picker(
                                v-model="recurrence.until"
                                @input="$refs.calendarMenu.save(recurrence.until)"
                                :allowed-dates="afterToday"
                                no-title
                              )
                      //- Option 3 - Ends after a certain number of occurrences
                      v-flex(xs12)
                        v-layout(align-center wrap)
                          v-flex(xs3)
                            v-radio(:value="2" label="After")
                          v-flex(xs5)
                            v-text-field.pt-0(
                              :disabled="recurrence.endType !== 2"
                              v-model="recurrence.count"
                              type="number"
                              min="1"
                              suffix="occurrence"
                            )

        //- The confirm and cancel buttons
        v-card-actions
          v-spacer
          v-btn.primary(flat :disabled="isDisabled()" @click="handleClickedDone()") Done
          v-btn(flat @click="handleClickCancel()") Cancel

    v-select(
      :items="options"
      :item-text="item => item.name"
      v-model="sendRecurring"
      label="Recurring Schedule"
      prepend-icon="cached"
      return-object
    )
    //- Option 3 - Ends after a certain number of occurrences
    v-flex(v-if="recurrence")
      span Ends
      v-radio-group(
        v-model="recurrence.endType"
        :rules='endTypeRules'
      )
        v-layout(wrap)
          //- Option 2 - Ends at a certain date
          v-flex(xs12  v-if="!isCampaignSetup")
            v-layout(align-center wrap)
              v-flex(xs3)
                v-radio(:value="1" label="On")
              v-flex(xs5)
                v-menu(
                  ref="calendarMenu"
                  :close-on-content-click="false"
                  :disabled="recurrence.endType !== 1"
                  transition="scale-transition"
                  min-width="290px"
                  lazy
                  offset-y
                  full-width
                )
                  v-text-field(
                    v-model="recurrence.until"
                    :disabled="recurrence.endType !== 1"
                    slot="activator"
                    label="Date"
                    prepend-icon="event"
                    readonly
                    clearable
                  )
                  v-date-picker(
                    v-model="recurrence.until"
                    @input="$refs.calendarMenu.save(recurrence.until)"
                    :allowed-dates="afterToday"
                    no-title
                  )
          //- Option 3 - Ends after a certain number of occurrences
          v-flex(xs12)
            v-layout(align-center wrap)
              v-flex(xs3 )
                v-radio(:value="2" label="After" :readonly="isCampaignSetup")
              v-flex(xs5)
                v-text-field.pt-0(
                  :disabled="recurrence.endType !== 2"
                  v-model="recurrence.count"
                  :rules='endTypeCountRules'
                  type="number"
                  min="1"
                  suffix="occurrence"
                )
</template>

<script>
import dateFns from 'date-fns';
import { afterToday } from '@/Core/filters/dates';
import Recurrence from '@/Core/models/recurrence';
import { FREQUENCY, WEEKDAYS } from '@/Core/config/recurrence';

export default {
  name: 'CustomRecurrenceDialog',
  data() {
    const values = {
      isActive: false,
      afterToday,
      startDate: this.value.startDate || this.initialDate || dateFns.parse(new Date()),
      // Initializes the starting point to the given custom object
      // Otherwise initialize to an empty object
      sendRecurring: this.constructInitialData(),
      // PreviousValidValue should mirror what the first value is.
      previousValidValue: this.constructInitialData(),
      recurrence: this.initializeRecurrence(),
      weekdays: WEEKDAYS,
      options: this.repeatOptions(),
      endTypeRules: [v => !!v || 'End recurrence type is required'],
      endTypeCountRules: [v => {
        return this.recurrence.endType === 2 || !!v ||  'Number of occurrences is required';
      }],
      rrCanceled: false,
    };
    return values;
  },
  props: {
    // The initial starting date desired to start doing the recurrence
    // This is important to obtain generate the RecurrenceRule accurately
    // Defaults to the day today if none is given.
    initialDate: {
      type: Date,
    },
    // The initialRule desired to be passed on to populate the recurrence picker, if exists.
    initialRule: {
      type: String,
      default: '',
    },
    manualStartDate: {
      type: Date,
    },
    value: {
      type: Object,
    },
    isCampaignSetup: {
      type: Boolean,
      default: false,
    }
  },
  computed: {
    /*
     * Reinitializes the recurring weekday to be the present day if nothing is chosen
     * It automatically pushes the day today if the selected length is zero. This is to ensure
     * at least one day is selected for a recurring interval.
     */
    weekdaySelected() {
      if (!this.recurrence || !this.recurrence.byday || !this.recurrence.byday.length) {
        this.selectByDayDefault();
      }
    },
    frequency() {
      return FREQUENCY;
    },
  },
  methods: {
    /*
     * Initializes the current chosen day to be today.
     */
    selectByDayDefault() {
      if (this.initialDate) {
        const currentWeekday = this.weekdays[this.initialDate.getDay()].id;
        this.recurrence.byday = [currentWeekday];
      }
    },
    /*
     * Returns a component with the initial data, whether if a rule is given.
     * Otherwise return an object with empty `id`, `name`, and `recurrenceRule`
     */
    constructInitialData() {
      // First priority, is check whether the v-model prop is populated already.
      // If it is, it should be the selected input.
      if (!this.rrCanceled && this.value && this.value.recurrenceRule && this.value.startDate) {
        const recurrence = new Recurrence(
          this.value.startDate,
          null,
          this.value.recurrenceRule,
          this.isCampaignSetup,
        );
        return {
          // 102 Denotes a pre-populated RecurrenceRule from the props.
          id: 102,
          name: recurrence.toDisplayString(),
          recurrenceRule: this.value.recurrenceRule,
        };
      // If the prop is not populated, fall back to the initial rule.
      } else if (this.initialRule) {
        const recurrence = new Recurrence(
          dateFns.format(new Date()),
          null,
          this.initialRule,
          this.isCampaignSetup,
        );
        return {
          // 102 Denotes a pre-populated RecurrenceRule from the props.
          id: 102,
          name: recurrence.toDisplayString(),
          recurrenceRule: this.initialRule,
        };
      }
      // if the custom recurrent rule is canceled, then select the "Do no repeat" recurrence.
      if(this.rrCanceled) {
        this.rrCanceled = false;
        return {
          id: -1,
          name: "Do not repeat",
          recurrenceRule: null,
        };
      }
      // If neither the v-model prop nor the initialRule is given,
      // don't select anything
      return {
        id: null,
        name: null,
        recurrenceRule: null,
      };
    },
    /*
     * Obtains the actual start date and the RecurrenceRule, changes the necessary variables,
     * and closes the modal
     */
    handleClickedDone() {
      this.startDate = this.recurrence.actualStartDate;
      const name = this.recurrence.toDisplayString();
      const recurrenceRule = this.recurrence.toRecurrenceRule();
      // 101 denotes a custom RecurrenceRule from the custom dialog box
      this.sendRecurring = { id: 101, name, recurrenceRule };
    },
    /*
     * When the cancel button is clicked, sets the current value to the value before it was clicked
     */
    handleClickCancel() {
      this.rrCanceled = true;
      this.previousValidValue = null
      this.sendRecurring = this.constructInitialData();
      this.rrCanceled = false;
      this.isActive = false;
    },
    /*
     * Checks whether the date is filled if the endType of 1 (ending at a certain date)
     * is selected
     */
    isEndTypeValid() {
      if (this.recurrence.endType === 1) { // This is when the recurrence endds on a certain date
        return !!this.recurrence.until;
      }
      return true;
    },
    /*
     * Conditions in which the confirm recurrence button is disabled
     */
    isDisabled() {
      const interval = parseInt(this.recurrence.interval, 10);
      const count = parseInt(this.recurrence.count, 10);
      return !interval || !count || interval < 0 || count < 0 || !this.isEndTypeValid();
    },
    /*
     * Returns a list of the most-used options for recurring intervals,
     */
    repeatOptions() {
      const now = dateFns.format(this.initialDate);
      const weekDaySymbol = dateFns.format(now, 'dd').toUpperCase();
      const weekNumber = dateFns.differenceInCalendarWeeks(now, dateFns.startOfMonth(now)) + 1;
      let defaults = [
        `FREQ=DAILY;INTERVAL=1;COUNT=${Recurrence.DEFAULT_COUNTS.DAILY}`,
        `FREQ=WEEKLY;INTERVAL=1;COUNT=${Recurrence.DEFAULT_COUNTS.WEEKLY};BYDAY=${weekDaySymbol}`,
      ]
      if (!this.isCampaignSetup) {
        defaults.push(`FREQ=MONTHLY;INTERVAL=1;COUNT=${Recurrence.DEFAULT_COUNTS.MONTHLY};BYSETPOS=${weekNumber};BYDAY=${weekDaySymbol}`)
      }
      defaults.push(`FREQ=YEARLY;INTERVAL=1;COUNT=${Recurrence.DEFAULT_COUNTS.YEARLY}`)
      defaults.push(`FREQ=WEEKLY;INTERVAL=1;COUNT=${Recurrence.DEFAULT_COUNTS.WEEKLY};BYDAY=WE,MO,TU,TH,FR`)
      // Pushes the default RRules into the option list
      const options = defaults.map((d, i) => {
        const r = new Recurrence(now, null, d, this.isCampaignSetup);
        return { id: i + 1, name: r.toDisplayString(), recurrenceRule: d };
      });
      // Pushes the custom selected RRule if exists
      if (this.initialRule || this.value.recurrenceRule) {
        options.push(this.constructInitialData());
      }
      // Pushes the custom dialog bar toggle
      options.unshift({
        id: -1,
        name: 'Do not repeat',
        recurrenceRule: '',
      });
      options.push({
        id: 100,
        name: 'Custom...',
        recurrenceRule: this.initialRule,
      });
      return options;
    },
    /*
     * Calls the recurrence constructor, and sets its state to the current RecurrenceRule if given.
     */
    initializeRecurrence() {
      let recurrenceRule;
      // Checks the previous valid value that the component has seen, and use that as
      // the initial state. Using this.sendRecurring or this.value wouldn't work because
      // the v-select component has already changed the value of those fields.
      if (this.previousValidValue && !!this.previousValidValue.recurrenceRule) {
        // eslint-disable-next-line prefer-destructuring
        recurrenceRule = this.previousValidValue.recurrenceRule;
      } else {
        recurrenceRule = this.initialRule;
      }
      if (!!recurrenceRule && recurrenceRule.length > 0) {
        return new Recurrence(
          dateFns.format(this.initialDate),
          null,
          recurrenceRule,
          this.isCampaignSetup,
        );
      }
    },
  },
  watch: {
    isActive(value) {
      if(!value) this.recurrence = this.initializeRecurrence();
    },

    manualStartDate: {
      handler(value) {
        if (value !== this.startDate) {
          this.initialDate = value
          this.recurrence = null
          this.options = this.repeatOptions();
        }
      }
    },

    recurrence: {
      handler(value) {
        if (value && this.sendRecurring && value.toRecurrenceRule() !== this.sendRecurring.recurrenceRule && this.sendRecurring.id !== 100 ) {
          let id = 102;
          if (value.toRecurrenceRule().includes('UNTIL')) {
            id = 101;
          }
          const result = {
            id,
            name: value.toDisplayString(),
            recurrenceRule: value.toRecurrenceRule(),
          };
          this.sendRecurring = result;
        }
      },
      deep: true,
    },

    /*
     * Watches the current reccuring interval, and opens the custom dialog
     * if the custom option is chosen. Otherwise, simply set the RecurrenceRule to the
     * desired pre-defined rule.
     */
    sendRecurring: {
      handler(value) {
        if (value) {
          if (value.id === 100) {
            this.isActive = true;
            if (!this.recurrence) {
              value.recurrenceRule = `FREQ=DAILY;INTERVAL=1;COUNT=${Recurrence.DEFAULT_COUNTS.DAILY}`
            }
          // If the desired value is the custom value, add that to the array of options,
          // and select that element.
          } else if (value.id === 101) {
            const basicOptions = this.repeatOptions();
            const lastElement = basicOptions.pop();
            basicOptions.push(value);
            basicOptions.push(lastElement);
            this.options = basicOptions;
            this.previousValidValue = this.sendRecurring;
            this.isActive = false;
          } else {
            this.option = this.repeatOptions();
            this.previousValidValue = this.sendRecurring;
            this.recurrence = null
          }

          if (value.recurrenceRule) {
            this.recurrence = new Recurrence(
              this.recurrence ? this.recurrence.actualStartDate : this.initialDate,
              null,
              value.recurrenceRule,
              this.isCampaignSetup,
            );

            this.$emit('input', {
              name: value.name,
              recurrenceRule: value.recurrenceRule,
              startDate: this.startDate,
            });
          }

          // If the selection to display custom option is clicked, show the dialog
          // 100 denotes that the dialog box is currently open and the user is customizing
          if (dateFns.getYear(this.initialDate) === 1969 && value.recurrenceRule !== null){
            this.$emit('initDueDate');
          }
        }
      },
      deep: true,
    },
    /*
     * Watches the initial date, and updates necessary variables if it changes.
     */
    initialDate(value, old) {
      this.initialDate = value;
      this.options = this.repeatOptions();
      this.sendRecurring = this.constructInitialData();
      this.previousValidValue = this.constructInitialData();
    },
  },
};
</script>
